import {
  ButtonBase,
  CircularProgress,
  Fade,
  IconButton,
} from '@material-ui/core';
import { Fullscreen, FullscreenExit } from '@material-ui/icons';
import { Color, ColorDynamic, Inline } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { lazy, Suspense, useState } from 'react';
import defaultImage from 'shared/icons/assets/black_default_image.png';
import { ErrorIcon } from 'shared/icons/ErrorIcon';
import { PlayIcon } from 'shared/icons/PlayIcon';
import styled from 'styled-components';
import { PhotoDTO, VideoDTO } from '../../carrier/loads/data/LoadDTO';
import { ReactNode, useMemo } from 'react';
import type { Slide } from 'yet-another-react-lightbox';
import { filterMediaByType, parsePhotoSlide } from 'shared/utils/MediaUtils';
const PhotoGallery = lazy(() => import('./PhotoGallery'));

const ErrorText = styled.div`
  color: ${Color.White};
`;

const PhotoViewerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  height: 100%;
`;

const NonVehiclePhotoAlert = styled.div`
  position: absolute;
  z-index: 2;
  top: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${ColorDynamic.Dark500};
  flex-direction: row;
  padding: 4px 8px;
  gap: 4px;
  border-radius: 8px;
  color: ${ColorDynamic.White};
  height: 36px;
  left: 50%;
  transform: translateX(-50%);
`;

export interface PhotoViewerProps {
  url: string;
  selectedIndex: number;
  total: number;
  photos: PhotoDTO[];
  inlinePhotos: Array<{
    id: string;
    url: string;
    thumbnail_url: string;
    is_vehicle_detected: boolean | null;
    is_video: boolean;
  }>;
  videos?: VideoDTO[];
  isVideo?: boolean;
  onClose: (index: number) => void;
  onView?: (type: 'image' | 'video' | undefined) => void;
  onModeChange?: () => void;
}

const PlayButton = styled(ButtonBase)`
  &.MuiButtonBase-root {
    width: 80px;
    height: 80px;
    border-radius: 50%;
  }
`;

const VideoButton = styled.div`
  position: absolute;
  right: 0;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const ButtonContainer = styled(Box)<{ width?: string; height?: string }>`
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  width: ${({ width = '80px' }) => width};
  height: ${({ height = '80px' }) => height};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VideoCounter = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Img = styled.img`
  object-fit: cover;
`;

const StyledPlayIcon = styled(PlayIcon)`
  width: 34px;
  height: 32px;
`;

export function PhotoViewer({
  url,
  photos,
  selectedIndex,
  total,
  videos,
  isVideo,
  onClose,
  inlinePhotos,
  onView,
  onModeChange,
}: PhotoViewerProps) {
  const [currentSlideIndex, setCurrentSlideIndex] =
    useState<number>(selectedIndex);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isVehicleNotDetected =
    inlinePhotos[currentSlideIndex]?.is_vehicle_detected === false;

  const title = isVehicleNotDetected && (
    <NonVehiclePhotoAlert>
      <ErrorIcon />
      Possibly Not a Vehicle
    </NonVehiclePhotoAlert>
  );

  const slides = useSlides({ photos, videos, title });

  function openFullscreen() {
    setIsFullscreen(true);
    setCurrentSlideIndex(selectedIndex);
  }

  return (
    <>
      {isFullscreen && (
        <Suspense fallback={<CircularProgress />}>
          <PhotoGallery
            slides={slides}
            currentSlideIndex={currentSlideIndex}
            setCurrentSlideIndex={setCurrentSlideIndex}
            onClose={(index) => {
              setIsFullscreen(false);
              onClose(index);
            }}
            onView={onView}
            onModeChange={onModeChange}
          />
        </Suspense>
      )}
      <PhotoViewerWrapper>
        <Box position="absolute">
          <Fade in={!isLoaded}>
            <CircularProgress color="inherit" />
          </Fade>
        </Box>
        <Fade in={isLoaded}>
          <PhotoViewerWrapper>
            {isVideo && (
              <VideoButton
                onClick={() => {
                  openFullscreen();
                }}
              >
                <ButtonContainer>
                  <PlayButton onClick={openFullscreen}>
                    <StyledPlayIcon />
                  </PlayButton>
                </ButtonContainer>
              </VideoButton>
            )}
            {isVideo ? (
              <VideoCounter
                borderRadius="small"
                backgroundColor="Dark300"
                color="White"
                paddingLeft="xsmall"
                paddingRight="xsmall"
                position="absolute"
                top="16px"
              >
                {selectedIndex + 1} of {total}
              </VideoCounter>
            ) : (
              <Box
                position="absolute"
                borderRadius="small"
                backgroundColor="Dark300"
                color="White"
                paddingLeft="xsmall"
                paddingRight="xsmall"
                top="16px"
                left="16px"
              >
                {selectedIndex + 1} of {total}
              </Box>
            )}

            {isVehicleNotDetected && (
              <Box
                position="absolute"
                top="16px"
                backgroundColor="Dark400"
                borderRadius="medium"
                paddingTop="xxsmall"
                paddingBottom="xxsmall"
                paddingRight="xsmall"
                paddingLeft="xsmall"
                color="White"
              >
                <Inline verticalAlign="center" space="xxsmall">
                  <ErrorIcon fontSize="small" />
                  <ErrorText>Possibly Not a Vehicle</ErrorText>
                </Inline>
              </Box>
            )}
            {!isVideo && (
              <Box
                position="absolute"
                right="16px"
                top="16px"
                backgroundColor="Dark300"
                borderRadius="small"
              >
                <IconButton
                  aria-label="toggle fullscreen"
                  onClick={openFullscreen}
                >
                  {isFullscreen ? (
                    <FullscreenExit htmlColor={Color.White} />
                  ) : (
                    <Fullscreen htmlColor={Color.White} />
                  )}
                </IconButton>
              </Box>
            )}
            <Img
              src={url}
              width="100%"
              height="100%"
              alt={`gallery main image ${selectedIndex + 1}`}
              onLoad={() => {
                setIsLoaded(true);
              }}
              onError={(e) => {
                e.currentTarget.src = defaultImage;
              }}
            />
          </PhotoViewerWrapper>
        </Fade>
      </PhotoViewerWrapper>
    </>
  );
}

interface SlidesProps {
  photos: PhotoDTO[];
  videos?: VideoDTO[];
  title: ReactNode;
}

function parseVideoSlide(video: VideoDTO) {
  return {
    type: 'video' as const,
    width: 1280,
    height: 720,
    poster: video.thumbnail_url,
    autoplay: true,
    sources: [{ src: video.url, type: 'video/mp4' }],
  };
}

function useSlides({ photos, videos, title }: SlidesProps) {
  return useMemo(() => {
    const photosList = photos.map((image) => parsePhotoSlide(image, title));
    const firstPhoto = photosList.at(0) as Slide;

    const pickUpVideos = filterMediaByType(videos, 'Pickup').map(
      parseVideoSlide,
    );
    const pickUpPhotos = photosList
      .slice(1, photos.length)
      .filter((photo) => photo.mediaType === 'Pickup');

    const deliveryVideos = filterMediaByType(videos, 'Delivery').map(
      parseVideoSlide,
    );
    const deliveryPhotos = photosList.filter(
      (photo) => photo.mediaType === 'Delivery',
    );

    return [
      firstPhoto,
      ...pickUpVideos,
      ...pickUpPhotos,
      ...deliveryVideos,
      ...deliveryPhotos,
    ];
  }, [videos, photos, title]);
}
